import {
  Breadcrumbs,
  Button,
  Icon,
  LinkButton,
  TextFading
} from '@yes.technology/react-toolkit'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import GenericObjectCardButton from 'component/GenericObjectCard/GenericObjectCardButton'
import SitemodelModal from 'component/SitemodelModal/SitemodelModal'
import { useObjectclass } from 'objectclass/shared'
import { useLocation } from 'react-router'

import getOutputNode from './helpers/getOutputNode'
import useTaskStatusActions from './hooks/useTaskStatusActions/useTaskStatusActions'
import SyntheticTaskActions from './SyntheticTaskActions'
import { TaskActionsContainer, TaskActionsRow } from './TaskObjectCard.styles'
import { TaskActionsProps } from './TaskObjectCard.types'

const TaskActions = ({
  statusUuid,
  taskUuid,
  statusActions,
  classification,
  changeStatus,
  objectclassUuid,
  des,
  returnOnFinish = false,
  taskmodelUuid,
  workflow,
  buttonProps,
  visualizeUrl = '',
  variant = 'default'
}: TaskActionsProps) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { objectclass } = useObjectclass({ objectclassUuid })

  const handleClose = () => setIsModalOpen(false)

  const visualizeBreadcrumbRoutes = useMemo(
    () => [
      { description: objectclass?.des ?? '', url: '' },
      { description: des, url: '' }
    ],
    [objectclass?.des, des]
  )

  const relativeEncodedUrl = encodeURIComponent(
    location.pathname + location.search + location.hash
  )
  const executeHref = `/${window.taskExecutorSitemodelUuid}?taskUuid=${taskUuid}&returnUrl=${relativeEncodedUrl}&returnOnFinish=${returnOnFinish.toString()}`

  const visualizeDestinationUrl = visualizeUrl
    ? `${visualizeUrl}?uuid_objectclass=${objectclassUuid}&object-uuid=${taskUuid}`
    : `/${window.objectDetailsSitemodelUuid}?uuid_objectclass=${objectclassUuid}&object-uuid=${taskUuid}`

  const { displayedStatusActions, showOnlyVisualizeButton } =
    useTaskStatusActions(statusActions, classification)

  const visualizeModal = isModalOpen && (
    <SitemodelModal
      title={
        <TextFading>
          <Breadcrumbs
            color='black'
            boldLastItem={false}
            spaceBetween='8px'
            fontStyle='italic'
            heightClass='large'
            separator='|'
            routes={visualizeBreadcrumbRoutes}
          />
        </TextFading>
      }
      completeURL={visualizeDestinationUrl}
      onClose={handleClose}
    />
  )

  if (variant === 'synthetic') {
    return (
      <SyntheticTaskActions
        statusUuid={statusUuid}
        displayedStatusActions={displayedStatusActions}
        showOnlyVisualizeButton={showOnlyVisualizeButton}
        changeStatus={changeStatus}
        executeHref={executeHref}
        buttonProps={buttonProps}
        taskmodelUuid={taskmodelUuid}
        workflow={workflow}
        visualizeModal={visualizeModal}
        onOpenVisualize={() => setIsModalOpen(true)}
      />
    )
  }

  const isWaitingInteraction =
    window.waitingInteractionStatusUuid === statusUuid

  const visualizeButton = (
    <Button
      key='view'
      variant='secondary'
      heightClass='small'
      style={{ width: '24px', minWidth: '24px' }}
      aria-label={t('task.action.view')}
      onClick={() => setIsModalOpen(true)}
    >
      <Icon iconName='Visualization' icSize='small' />
    </Button>
  )

  const outputNode = getOutputNode(taskmodelUuid, workflow?.[0])

  const outputTaskVisualizeButton = outputNode?.type ===
    'output_task_nodemodel' && (
    <LinkButton
      key='view'
      variant='secondary'
      heightClass='small'
      aria-label={t('task.action.view')}
      target='_blank'
      rel='noopener noreferrer'
      href={`${window.waInteractionUrl}${outputNode.handler.interactionmodel_uuid}?uuid_workflow=${workflow?.[0].uuid}&processValuesVariable=${outputNode.handler.target_container_variable}`}
    >
      {t('task.action.view')}
    </LinkButton>
  )

  const executeButton =
    buttonProps !== undefined ? (
      <GenericObjectCardButton
        buttonProps={buttonProps}
        href={executeHref}
        label={t('task.action.execute')}
        variant='primary'
      />
    ) : (
      <LinkButton
        rel='noopener noreferrer'
        variant='primary'
        heightClass='small'
        href={executeHref}
      >
        {t('task.action.execute')}
      </LinkButton>
    )

  if (showOnlyVisualizeButton && !isWaitingInteraction) {
    return (
      <TaskActionsContainer>
        <Button
          key='view'
          variant='secondary'
          heightClass='small'
          style={{ width: '100%' }}
          aria-label={t('task.action.view')}
          onClick={() => setIsModalOpen(true)}
        >
          <Icon iconName='Visualization' icSize='small' />
        </Button>
        {visualizeModal}
      </TaskActionsContainer>
    )
  }

  return (
    <TaskActionsContainer>
      {isWaitingInteraction && (
        <TaskActionsRow>
          {executeButton}
          {!displayedStatusActions.length && visualizeButton}
        </TaskActionsRow>
      )}
      {outputTaskVisualizeButton && (
        <TaskActionsRow>{outputTaskVisualizeButton}</TaskActionsRow>
      )}
      {displayedStatusActions.map((statusAction, i) => (
        <TaskActionsRow
          key={`${statusAction.destination_status[0].code}_${i}`}
          $fullWidth={i === displayedStatusActions.length - 1}
        >
          <Button
            variant={!isWaitingInteraction && i === 0 ? 'primary' : 'secondary'}
            heightClass='small'
            onClick={() =>
              changeStatus(statusAction.destination_status[0].code)
            }
          >
            {statusAction.des}
          </Button>
          {i === displayedStatusActions.length - 1 && visualizeButton}
        </TaskActionsRow>
      ))}
      {visualizeModal}
    </TaskActionsContainer>
  )
}

export default TaskActions
